import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClockIcon, TagIcon, ClipboardCheckIcon, ChevronRightIcon, MicIcon, SendIcon, PlusIcon, PlayIcon, PauseIcon, FileTextIcon, ImageIcon, VideoIcon, Maximize2Icon, ChevronDownIcon, XIcon } from 'lucide-react';
import { mockData } from '../utils/mockData';
import { Button } from '../components/common/Button';
import { Dropdown } from '../components/common/Dropdown';
export const IncidentSurface = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const transcriptionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const draftTypes = ['Pass On', 'Arrest', 'Use of Force', 'Traffic', 'Incident'];
  const sampleTranscription = "Arriving on scene at the rear of the property. I'm observing fresh forced entry marks on the sliding glass door. There's a discarded pry bar in the grass near the patio. Proceeding with caution. Dispatch, be advised I'm entering the structure now.";
  useEffect(() => {
    if (isReviewMode && bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [isReviewMode]);
  useEffect(() => {
    if (isVoiceMode) {
      setTranscribedText('');
      const words = sampleTranscription.split(' ');
      let currentWordIndex = 0;
      transcriptionIntervalRef.current = setInterval(() => {
        if (currentWordIndex < words.length) {
          setTranscribedText(prev => prev + (prev ? ' ' : '') + words[currentWordIndex]);
          currentWordIndex++;
        } else {
          if (transcriptionIntervalRef.current) clearInterval(transcriptionIntervalRef.current);
        }
      }, 100 + Math.random() * 100);
    } else {
      if (transcriptionIntervalRef.current) clearInterval(transcriptionIntervalRef.current);
    }
    return () => {
      if (transcriptionIntervalRef.current) clearInterval(transcriptionIntervalRef.current);
    };
  }, [isVoiceMode]);
  const incident = mockData.sidebar.fieldNotes.find(i => i.id === id) || mockData.sidebar.activeIncidents.find(i => i.id === id);
  const incidentNotes = mockData.sidebar.fieldNotesFeed.filter(note => note.incident === incident?.title).map(note => ({
    ...note,
    itemType: 'note'
  }));
  // Add rich media items
  const timelineItems = [...incidentNotes, {
    id: 'media-1',
    itemType: 'document',
    timestamp: '14:28',
    title: 'Scanned Documents',
    files: ['Driver License - J. Doe', 'Vehicle Registration']
  }, {
    id: 'media-2',
    itemType: 'album',
    timestamp: '14:35',
    title: 'Scene Photos',
    count: 12,
    images: ['https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=400&q=80', 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=400&q=80', 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=400&q=80', 'https://images.unsplash.com/photo-1509822929063-6b6cfc9b42f2?auto=format&fit=crop&w=400&q=80']
  }, {
    id: 'media-3',
    itemType: 'video',
    timestamp: '14:42',
    title: 'Bodycam Footage',
    duration: '04:12',
    officer: 'Ofc. Smith #1234'
  }].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  const reviewQuestions = [{
    id: 1,
    question: 'What are the primary safety concerns identified at the scene?',
    answer: 'Primary safety concerns include potential structural instability at the rear entrance and unsecured perimeter fencing.'
  }, {
    id: 2,
    question: 'Describe any suspicious persons or vehicles observed by witnesses.',
    answer: "Witnesses reported a silver sedan with partial plate 'B24' idling near the property shortly before the incident."
  }, {
    id: 3,
    question: 'What immediate follow-up actions are required for this investigation?',
    answer: 'Secure the perimeter, request structural assessment, and canvass neighboring properties for doorbell camera footage.'
  }];
  if (!incident) return <div className="p-8 text-white" data-id="element-317">Incident not found</div>;
  return <main className="flex-grow flex flex-col h-full min-h-0 overflow-hidden relative bg-[#0a1120]" data-id="element-318">
      {/* Transcription Surface Overlay */}
      {isVoiceMode && <div className="absolute inset-0 z-50 bg-command-app/95 backdrop-blur-xl flex items-center justify-center p-8 animate-fade-in" data-id="element-319">
          <div className="w-full max-w-[800px] bg-command-panel border border-command-border-soft rounded-3xl p-12 shadow-2xl relative animate-slide-up" data-id="element-320">
            <button onClick={() => setIsVoiceMode(false)} className="absolute top-6 right-6 p-2 hover:bg-command-input rounded-full text-command-text-muted hover:text-white transition-colors" data-id="element-321">
              <XIcon size={24} data-id="element-322" />
            </button>

            <div className="flex items-center gap-3 mb-8" data-id="element-323">
              <div className="w-3 h-3 rounded-full bg-[#ff4444] animate-pulse" data-id="element-324" />
              <span className="text-xs font-bold text-[#888888] uppercase tracking-[0.2em]" data-id="element-325">
                Live Transcription
              </span>
            </div>

            <div className="min-h-[200px]" data-id="element-326">
              <p className="text-3xl md:text-4xl font-medium text-white leading-tight" data-id="element-327">
                {transcribedText}
                <span className="inline-block w-1 h-8 bg-[#4a90e2] ml-2 animate-pulse align-middle" data-id="element-328" />
              </p>
            </div>

            <div className="mt-12 flex justify-center" data-id="element-329">
              <Button variant="primary" size="lg" onClick={() => {
            alert('Transcription saved to incident.');
            setIsVoiceMode(false);
          }} data-id="element-330">
                Save Note
              </Button>
            </div>
          </div>
        </div>}

      {/* Hero Header */}
      <div className="flex-shrink-0 p-8 border-b border-command-border bg-[#0a1120]/80 backdrop-blur-md z-20" data-id="element-331">
        <div className="max-w-[1000px] mx-auto flex justify-between items-end" data-id="element-332">
          <div data-id="element-333">
            <div className="flex items-center gap-2 mb-3" data-id="element-334">
              <div className="w-2 h-2 rounded-full bg-[#f59e0b]" data-id="element-335" />
              <span className="text-xs font-bold text-[#f59e0b] uppercase tracking-[0.2em]" data-id="element-336">
                {incidentNotes.length.toString().padStart(2, '0')} ACTIVE NOTES
                FOR THIS INCIDENT
              </span>
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight" data-id="element-337">
              {incident.title}
            </h1>
            <p className="text-[var(--text-tertiary)] mt-1" data-id="element-338">
              Opened on {incident.date}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-339">
            <Button variant={isReviewMode ? 'primary' : 'secondary'} icon={ClipboardCheckIcon} onClick={() => {
            setIsReviewMode(!isReviewMode);
            setSelectedQuestionId(null);
          }} data-id="element-340">
              {isReviewMode ? 'Close Review' : 'Review'}
            </Button>

            <Dropdown width="w-48" align="right" trigger={<button className="flex items-center gap-2 px-4 py-2 bg-[#4a90e2] hover:bg-[#5da0eb] text-white rounded-lg font-semibold transition-all shadow-lg shadow-[#4a90e2]/20" data-id="element-342">
                  <ClipboardCheckIcon size={18} data-id="element-343" />
                  <span data-id="element-344">Report</span>
                  <ChevronDownIcon size={14} className="ml-1 opacity-60" data-id="element-345" />
                </button>} data-id="element-341">
              <div className="py-1" data-id="element-346">
                <div className="px-3 py-1.5 text-[10px] font-bold text-command-text-muted uppercase tracking-wider" data-id="element-347">
                  Generate Report
                </div>
                {draftTypes.map(type => <button key={type} onClick={() => navigate(`/report/${id}`)} className="w-full text-left px-3 py-2 text-xs text-[#e0e0e0] hover:bg-white/5 transition-colors" data-id="element-348">
                    {type}
                  </button>)}
              </div>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div ref={scrollContainerRef} className="flex-grow overflow-y-auto custom-scrollbar min-h-0" data-id="element-349">
        <div className="p-8 pb-8 max-w-[1000px] mx-auto space-y-8" data-id="element-350">
          {/* Incident Timeline Section */}
          <div className="space-y-8" data-id="element-351">
            <div className="relative ml-8 pl-12 border-l border-[var(--timeline-line)] space-y-8 py-4" data-id="element-352">
              {timelineItems.map((item: any) => <div key={item.id} className="relative group/item" data-id="element-353">
                  {/* Timeline Dot - Aligned with metadata row */}
                  <div className={`absolute -left-[57px] top-7 w-4 h-4 rounded-full bg-[#0a1120] border-2 z-10 group-hover/item:scale-110 transition-transform ${item.itemType === 'note' ? 'border-[var(--timeline-dot)]' : 'border-[#4a90e2]'}`} data-id="element-354" />

                  {item.itemType === 'note' ? <div className="bg-[#151A22]/40 border border-command-border rounded-2xl px-8 py-6 min-h-[118px] hover:border-white/10 hover:bg-[#151A22]/60 transition-all" data-id="element-355">
                      <div className="flex justify-between items-start mb-4" data-id="element-356">
                        <div className="flex items-center gap-4" data-id="element-357">
                          <div className="flex items-center gap-2" data-id="element-358">
                            <span className="text-sm font-medium text-[#f59e0b]" data-id="element-359">
                              {item.timestamp}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-360">
                            <span className="text-[10px] font-bold text-[#f59e0b] uppercase tracking-widest" data-id="element-361">
                              {item.type === 'typed' ? 'MANUAL ENTRY' : 'TRANSCRIBED'}
                            </span>
                          </div>
                        </div>
                        {item.type === 'transcript' && <button className="text-[#444444] hover:text-[#e0e0e0] transition-colors" data-id="element-362">
                            <PlayIcon size={20} fill="currentColor" data-id="element-363" />
                          </button>}
                      </div>
                      <p className="text-lg text-[#e0e0e0] leading-relaxed font-medium" data-id="element-364">
                        {item.content}
                      </p>
                    </div> : item.itemType === 'document' ? <div className="bg-[#151A22]/40 border border-[#4a90e2]/30 rounded-2xl px-8 py-6 hover:bg-[#151A22]/60 transition-all" data-id="element-365">
                      <div className="flex justify-between items-start mb-4" data-id="element-366">
                        <div className="flex items-center gap-4" data-id="element-367">
                          <span className="text-sm font-medium text-[#f59e0b]" data-id="element-368">
                            {item.timestamp}
                          </span>
                          <span className="text-[10px] font-bold text-[#4a90e2] uppercase tracking-widest" data-id="element-369">
                            DOCUMENT SCAN
                          </span>
                        </div>
                        <FileTextIcon size={18} className="text-[#4a90e2]" data-id="element-370" />
                      </div>
                      <div className="space-y-3" data-id="element-371">
                        {item.files.map((file: string, idx: number) => <div key={idx} className="flex items-center justify-between p-3 bg-white/5 border border-white/5 rounded-xl hover:bg-white/10 transition-colors cursor-pointer group/file" data-id="element-372">
                            <div className="flex items-center gap-3" data-id="element-373">
                              <div className="w-8 h-8 rounded-lg bg-[#4a90e2]/10 flex items-center justify-center text-[#4a90e2]" data-id="element-374">
                                <FileTextIcon size={16} data-id="element-375" />
                              </div>
                              <span className="text-sm font-medium text-white/90" data-id="element-376">
                                {file}
                              </span>
                            </div>
                            <Maximize2Icon size={14} className="text-command-text-muted opacity-0 group-hover/file:opacity-100 transition-opacity" data-id="element-377" />
                          </div>)}
                      </div>
                    </div> : item.itemType === 'album' ? <div className="bg-[#151A22]/40 border border-[#4a90e2]/30 rounded-2xl px-8 py-6 hover:bg-[#151A22]/60 transition-all" data-id="element-378">
                      <div className="flex justify-between items-start mb-4" data-id="element-379">
                        <div className="flex items-center gap-4" data-id="element-380">
                          <span className="text-sm font-medium text-[#f59e0b]" data-id="element-381">
                            {item.timestamp}
                          </span>
                          <span className="text-[10px] font-bold text-[#4a90e2] uppercase tracking-widest" data-id="element-382">
                            PHOTO ALBUM
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs font-bold text-command-text-muted" data-id="element-383">
                          <ImageIcon size={14} data-id="element-384" />
                          <span data-id="element-385">{item.count} PHOTOS</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-3" data-id="element-386">
                        {item.images.map((img: string, idx: number) => <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-white/5 group/img cursor-pointer" data-id="element-387">
                            <img src={img} alt="Scene" className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-110" data-id="element-388" />
                            {idx === 3 && <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[2px]" data-id="element-389">
                                <span className="text-sm font-bold text-white" data-id="element-390">
                                  +{item.count - 4}
                                </span>
                              </div>}
                          </div>)}
                      </div>
                    </div> : <div className="bg-[#151A22]/40 border border-[#4a90e2]/30 rounded-2xl overflow-hidden hover:bg-[#151A22]/60 transition-all" data-id="element-391">
                      <div className="px-8 py-6" data-id="element-392">
                        <div className="flex justify-between items-start mb-4" data-id="element-393">
                          <div className="flex items-center gap-4" data-id="element-394">
                            <span className="text-sm font-medium text-[#f59e0b]" data-id="element-395">
                              {item.timestamp}
                            </span>
                            <span className="text-[10px] font-bold text-[#4a90e2] uppercase tracking-widest" data-id="element-396">
                              BODYCAM VIDEO
                            </span>
                          </div>
                          <VideoIcon size={18} className="text-[#4a90e2]" data-id="element-397" />
                        </div>
                        <div className="relative aspect-video rounded-xl overflow-hidden bg-black/40 border border-white/5 group/video cursor-pointer" data-id="element-398">
                          <div className="absolute inset-0 flex items-center justify-center" data-id="element-399">
                            <div className="w-16 h-16 rounded-full bg-[#4a90e2] flex items-center justify-center text-white shadow-xl shadow-[#4a90e2]/20 transition-transform group-hover/video:scale-110" data-id="element-400">
                              <PlayIcon size={32} fill="currentColor" data-id="element-401" />
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent" data-id="element-402">
                            <div className="flex items-center justify-between text-[10px] font-bold text-white/80 uppercase tracking-widest" data-id="element-403">
                              <span data-id="element-404">{item.officer}</span>
                              <span data-id="element-405">{item.duration}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>)}

              {/* Answered Q&A Panel - Integrated into Timeline */}
              {!isReviewMode && <div className="relative group/summary" data-id="element-406">
                  {/* Timeline Dot - Concluding dot */}
                  <div className="absolute -left-[57px] top-7 w-4 h-4 rounded-full bg-[#0a1120] border-2 border-[#4a90e2] z-10 group-hover/summary:scale-110 transition-transform" data-id="element-407" />

                  <div className="bg-[#151A22]/40 border border-command-border rounded-3xl overflow-hidden" data-id="element-408">
                    <div className="p-2" data-id="element-409">
                      {reviewQuestions.map(q => <div key={q.id} className="p-5 border-b border-command-border last:border-0" data-id="element-410">
                          <div className="flex gap-4" data-id="element-411">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4a90e2]/10 flex items-center justify-center text-[10px] font-bold text-[#4a90e2] border border-[#4a90e2]/20" data-id="element-412">
                              {q.id}
                            </div>
                            <div className="space-y-2" data-id="element-413">
                              <p className="text-sm font-semibold text-white/90" data-id="element-414">
                                {q.question}
                              </p>
                              <p className="text-sm text-[var(--text-tertiary)] leading-relaxed" data-id="element-415">
                                {q.answer}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* Investigative Review Section (Appears at bottom when active) */}
          {isReviewMode && <div className="space-y-6 animate-slide-up pt-8 border-t border-command-border" data-id="element-416">
              {reviewQuestions.map(q => <div key={q.id} onClick={() => setSelectedQuestionId(selectedQuestionId === q.id ? null : q.id)} className={`p-6 rounded-2xl border transition-all duration-300 cursor-pointer ${selectedQuestionId === q.id ? 'bg-[#151A22]/60 border-[#4a90e2] shadow-lg shadow-[#4a90e2]/5' : 'bg-[#151A22]/40 border-white/5 hover:border-white/10 hover:bg-[#151A22]/60'}`} data-id="element-417">
                  <div className="flex justify-between items-center" data-id="element-418">
                    <div className="flex items-center gap-4" data-id="element-419">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${selectedQuestionId === q.id ? 'bg-[#4a90e2] text-white' : 'bg-[#2a2a2a] text-command-text-muted'}`} data-id="element-420">
                        {q.id}
                      </div>
                      <p className="text-lg text-[#e0e0e0] font-medium" data-id="element-421">
                        {q.question}
                      </p>
                    </div>
                    <ChevronRightIcon size={20} className={`text-command-text-muted transition-transform duration-300 ${selectedQuestionId === q.id ? 'rotate-90 text-[#4a90e2]' : ''}`} data-id="element-422" />
                  </div>

                  {/* Textarea hidden by default until selected */}
                  {selectedQuestionId === q.id && <div className="mt-6 pt-6 border-t border-command-border animate-slide-down" onClick={e => e.stopPropagation()} data-id="element-423">
                      <div className="flex items-center gap-4 h-16" data-id="element-424">
                        <div className="flex-grow bg-[#151A22]/50 border border-command-border rounded-2xl px-5 flex items-center h-full" data-id="element-425">
                          <textarea autoFocus className="flex-grow bg-transparent focus:outline-none placeholder:text-command-text-muted text-lg py-2 resize-none h-10 overflow-hidden" placeholder="Type your answer here..." rows={1} data-id="element-426" />
                          <button className="rounded-full w-12 h-12 flex items-center justify-center transition-all bg-white/5 text-command-text-muted" data-id="element-427">
                            <SendIcon size={20} className="text-command-text-muted" data-id="element-428" />
                          </button>
                        </div>
                        <button className="rounded-full w-14 h-14 flex items-center justify-center transition-all flex-shrink-0 border bg-[#151A22] border-command-border hover:bg-white/5" data-id="element-429">
                          <MicIcon size={22} className="text-[#e0e0e0]" data-id="element-430" />
                        </button>
                      </div>
                    </div>}
                </div>)}
            </div>}
          <div ref={bottomRef} data-id="element-431" />
        </div>
      </div>

      {/* Sticky Chat Footer (only in timeline mode) */}
      {!isReviewMode && <div className="flex-shrink-0 p-8 pt-4 border-t border-command-border bg-[#0a1120]/80 backdrop-blur-md" data-id="element-432">
          <div className="max-w-[1000px] mx-auto flex items-center gap-4 h-16" data-id="element-433">
            <div className="flex-grow bg-[#151A22]/50 border border-white/5 rounded-2xl px-5 flex items-center h-full" data-id="element-434">
              <div className="flex items-center gap-4 w-full" data-id="element-435">
                <button className="text-command-text-muted hover:text-[#e0e0e0] transition-colors" title="Attach file" data-id="element-436">
                  <PlusIcon size={22} data-id="element-437" />
                </button>
                <textarea className="flex-grow bg-transparent focus:outline-none placeholder:text-command-text-muted text-lg py-2 resize-none h-10 overflow-hidden" placeholder="Add a note to this incident..." value={message} onChange={e => setMessage(e.target.value)} rows={1} data-id="element-438" />
                <button className={`rounded-full w-12 h-12 flex items-center justify-center transition-all ${message.trim() ? 'bg-[#4a90e2] text-white hover:bg-[#3a7bc2]' : 'bg-white/5 text-[#888888]'}`} disabled={!message.trim()} onClick={() => {
              alert(`Saving note: ${message}`);
              setMessage('');
            }} data-id="element-439">
                  <SendIcon size={20} className={message.trim() ? 'text-white' : 'text-command-text-muted'} data-id="element-440" />
                </button>
              </div>
            </div>

            <button onClick={() => setIsVoiceMode(!isVoiceMode)} className={`rounded-full w-14 h-14 flex items-center justify-center transition-all flex-shrink-0 border ${isVoiceMode ? 'bg-[#4a90e2] border-[#4a90e2] shadow-lg shadow-[#4a90e2]/20' : 'bg-[#151A22] border-command-border hover:bg-white/5'}`} data-id="element-441">
              <MicIcon size={22} className={isVoiceMode ? 'text-white' : 'text-[#e0e0e0]'} data-id="element-442" />
            </button>
          </div>
        </div>}
    </main>;
};